import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import withAuth from '../../../HOC/withAuth';
import { useTranslation } from '../../../t';
import Empty from '../../../components/Empty';
import CustomPagination from '../../../components/Pagination';
import WithDrawDialog from '../../../components/WithDrawDialog';
import InfoBar from '../InfoBar';
import C from '../../../colors';
import WithdrawalFeeNotice from '../../../components/WithdrawalFeeNotice';
import WithdrawalLimitNotice from '../../../components/WithdrawalLimitNotice';

const WithdrawIndex = inject('userStore')(observer(({ userStore }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ balance: 0, totalWithdrawn: 0, pendingAmount: 0, withdrawals: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await userStore.fetchWithdrawList(page);
      setData(res);
      setTotalPage(res.totalPages || 1);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { fetchData(); }, [page]);

  const cancelWithdraw = async (withdraw) => {
    if (!window.confirm(t('Confirm cancel withdrawal'))) return;
    setCancelingId(withdraw.id);
    try {
      await userStore.cancelWithdraw(withdraw.id);
      userStore.refreshInfo();
      await fetchData();
    } finally {
      setCancelingId(null);
    }
  };

  const statusStyle = status => {
    if (status === 'SUCCESS') return { background: C.successBg, color: C.successFg };
    if (status === 'RETURNED') return { background: C.dangerBg, color: C.dangerFg };
    if (status === 'ACCEPTED' || status === 'FRONZEN') return { background: C.warningBg, color: C.warningFg };
    return { background: C.neutralBg, color: C.neutralFg };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <InfoBar />
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 36px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 36, fontWeight: 400, color: C.ink }}>{t('Withdraw Management')}</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-testid="withdraw-refresh" onClick={fetchData} disabled={refreshing} style={{
              fontFamily: C.sans, fontSize: 13, fontWeight: 500, padding: '9px 14px', borderRadius: 7,
              background: '#fff', color: C.ink2, border: `1px solid ${C.hair2}`, cursor: refreshing ? 'default' : 'pointer',
            }}>↻ {refreshing ? t('Refreshing') : t('Refresh Status')}</button>
            <button data-testid="withdraw-request" onClick={() => setDialogOpen(true)} style={{
              fontFamily: C.sans, fontSize: 13, fontWeight: 500, padding: '9px 16px', borderRadius: 7,
              background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
              boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
            }}>{t('Request Withdraw')}</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[{ l: t('Available Balance'), v: `¥${Number(userStore.balance || 0).toFixed(2)}`, a: true, testId: 'withdraw-available-balance' },
            { l: t('Settling Balance'), v: `¥${Number(userStore.frozenBalance || 0).toFixed(2)}`, testId: 'withdraw-settling-balance' },
            { l: t('Total Withdrawn'), v: `¥${Number(data.totalWithdrawn || 0).toFixed(2)}`, testId: 'withdraw-total-withdrawn' },
            { l: t('Pending Amount'), v: `¥${Number(data.pendingAmount || 0).toFixed(2)}`, testId: 'withdraw-pending-amount' }].map(k => (
            <div key={k.l} data-testid={k.testId} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3, marginBottom: 8 }}>{k.l}</div>
              <div style={{ fontFamily: C.serif, fontSize: 28, color: k.a ? C.brand2 : C.ink }}>{k.v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3, marginBottom: 9 }}>
            费用说明
          </div>
          <WithdrawalFeeNotice />
        </div>

        <div style={{ marginBottom: 24 }}>
          <WithdrawalLimitNotice />
        </div>

        {(!data.withdrawals || data.withdrawals.length === 0) ? (
          <Empty message={t('No withdraw requests')}/>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: C.sans }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.hair}`, background: C.paper2 }}>
                  {[t('Transaction ID'), t('Amount'), t('Withdraw Method'), t('Status'), t('Request Date')].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.ink3, fontFamily: C.mono, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.withdrawals.map((r, i) => (
                  <tr key={r.id || i} data-testid={r.id ? `withdraw-row-${r.id}` : undefined} style={{ borderBottom: i < data.withdrawals.length - 1 ? `1px solid ${C.hair}` : 'none' }}>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink, fontFamily: C.mono }}>{r.id || r.transactionId}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink }}>¥{Number(r.amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink2 }}>{t(r.method || 'WeChat')}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, ...statusStyle(r.status) }}>{t(r.status || 'Pending')}</span>
                      {r.status === 'ACCEPTED' && (
                        <button data-testid={`withdraw-cancel-${r.id}`} disabled={cancelingId === r.id} onClick={() => cancelWithdraw(r)} style={{
                          marginLeft: 8, padding: '3px 8px', borderRadius: 7, border: `1px solid ${C.hair2}`,
                          background: '#fff', color: C.dangerFg, fontFamily: C.sans, fontSize: 11, cursor: 'pointer',
                        }}>{cancelingId === r.id ? t('Cancelling') : t('Cancel withdrawal')}</button>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink2 }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('zh-CN') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPage > 1 && <div style={{ marginTop: 16 }}><CustomPagination count={totalPage} page={page} onChange={(_, v) => setPage(v)}/></div>}
      </div>
      <WithDrawDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSuccess={fetchData}/>
    </div>
  );
}));

export default withAuth(WithdrawIndex);
